/**
 * Сайдбар режима ревью OSM-пачки: прогресс, действия, hotkeys.
 */
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { geometriesApi, osmImportsApi } from "@/services/api-v2.service";
import { useToast } from "@/hooks/use-toast";
import type { OsmReviewItem, OsmReviewQueueResponse } from "@/types/osm-import";
import { messageFromThrownApiError } from "@/lib/geometry-api-errors";

interface OsmReviewPanelProps {
  batchId: string;
  onClose: () => void;
  onPublished: () => void;
  onQueueLoaded?: (items: OsmReviewItem[]) => void;
  onFocusItem?: (item: OsmReviewItem) => void;
}

const REVIEW_STATUS_LABELS: Record<string, string> = {
  draft: "Черновик",
  approved: "Подтверждено",
  edited: "Изменено",
  rejected: "Отклонено",
  published: "Опубликовано",
};

function reviewStatusLabel(status: string): string {
  return REVIEW_STATUS_LABELS[status] ?? status;
}

export function OsmReviewPanel({
  batchId,
  onClose,
  onPublished,
  onQueueLoaded,
  onFocusItem,
}: OsmReviewPanelProps) {
  const { toast } = useToast();
  const [queue, setQueue] = useState<OsmReviewQueueResponse | null>(null);
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    const data = await osmImportsApi.reviewQueue(batchId, { offset: 0, limit: 500 });
    setQueue(data);
    onQueueLoaded?.(data.items);
    setIndex((i) => Math.min(i, Math.max(0, data.items.length - 1)));
  }, [batchId, onQueueLoaded]);

  useEffect(() => {
    reload().catch((e) =>
      toast({
        title: "Ошибка загрузки ревью",
        description: messageFromThrownApiError(e),
        variant: "destructive",
      }),
    );
  }, [reload, toast]);

  const current = queue?.items[index];

  const doAction = async (action: "approve" | "reject" | "reset") => {
    if (!current) return;
    setBusy(true);
    try {
      await geometriesApi.osmReview(current.geometry_id, {
        action,
        version: current.version,
      });
      await reload();
      setIndex((i) => Math.min(i + 1, Math.max(0, (queue?.items.length ?? 1) - 1)));
    } catch (e: any) {
      const status = e?.status ?? e?.response?.status;
      if (status === 409) {
        toast({
          title: "Конфликт версий",
          description: "Объект изменён, очередь обновлена",
          variant: "destructive",
        });
        await reload();
      } else {
        toast({
          title: "Ошибка",
          description: messageFromThrownApiError(e),
          variant: "destructive",
        });
      }
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === "a" || e.key === "A") void doAction("approve");
      if (e.key === "r" || e.key === "R") void doAction("reject");
      if (e.key === "ArrowRight") setIndex((i) => Math.min(i + 1, (queue?.items.length ?? 1) - 1));
      if (e.key === "ArrowLeft") setIndex((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, queue]);

  useEffect(() => {
    if (current) onFocusItem?.(current);
  }, [current, onFocusItem]);

  const done =
    queue?.items.filter((i) => i.status !== "draft").length ?? 0;
  const total = queue?.total ?? 0;

  return (
    <div className="flex flex-col gap-3 p-3 border-l bg-background/95 w-72 shrink-0">
      <div className="font-medium">Ревью OSM</div>
      <div className="text-sm text-muted-foreground">
        Прогресс: {done}/{total}
      </div>
      <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside leading-snug">
        <li>
          Просмотрите здания: «Подтвердить» / «Отклонить» (или сразу «Подтвердить всё»).
        </li>
        <li>
          Нажмите «Опубликовать» — подтверждённые появятся на карте, отклонённые удалятся.
        </li>
        <li>
          Чтобы отказаться от всей пачки — «Отменить импорт» (черновики удалятся).
        </li>
      </ol>
      {current && (
        <div className="text-sm space-y-1">
          <div className="font-medium truncate">{current.object_name}</div>
          <div className="text-muted-foreground">
            Статус: {reviewStatusLabel(current.status)}
            {current.osm_conflict ? " · конфликт OSM" : ""}
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          disabled={!current || busy}
          onClick={() => void doAction("approve")}
          title="Клавиша A"
        >
          Подтвердить (A)
        </Button>
        <Button
          size="sm"
          variant="destructive"
          disabled={!current || busy}
          onClick={() => void doAction("reject")}
          title="Клавиша R"
        >
          Отклонить (R)
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={!current || busy}
          onClick={() => void doAction("reset")}
          title="Вернуть в черновик / сбросить правки контура"
        >
          Сбросить
        </Button>
      </div>
      <Button
        size="sm"
        variant="secondary"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            await osmImportsApi.bulkReview(batchId, "approve_all");
            await reload();
          } catch (e) {
            toast({
              title: "Ошибка",
              description: messageFromThrownApiError(e),
              variant: "destructive",
            });
          } finally {
            setBusy(false);
          }
        }}
      >
        Подтвердить всё
      </Button>
      <Button
        size="sm"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            const res = await osmImportsApi.publish(batchId);
            toast({
              title: "Опубликовано",
              description: `На карту: ${res.published}, удалено отклонённых: ${res.removed}`,
            });
            onPublished();
          } catch (e: any) {
            const status = e?.status ?? e?.response?.status;
            toast({
              title: status === 403 ? "Нет права objects.edit" : "Ошибка публикации",
              description: messageFromThrownApiError(e),
              variant: "destructive",
            });
          } finally {
            setBusy(false);
          }
        }}
      >
        Опубликовать
      </Button>
      <Button
        size="sm"
        variant="outline"
        disabled={busy}
        onClick={async () => {
          if (!confirm("Отменить импорт и удалить черновики?")) return;
          setBusy(true);
          try {
            await osmImportsApi.cancel(batchId);
            onClose();
          } catch (e) {
            toast({
              title: "Ошибка",
              description: messageFromThrownApiError(e),
              variant: "destructive",
            });
          } finally {
            setBusy(false);
          }
        }}
      >
        Отменить импорт
      </Button>
      <Button size="sm" variant="ghost" onClick={onClose}>
        Закрыть
      </Button>
    </div>
  );
}
